/**
 * Two-layer crossfade stage. Renders the current scene (`children`, keyed
 * by `sceneKey`) on top of the previous scene, and animates the swap using
 * a TransitionPlan whenever `sceneKey` changes. When the same scene
 * re-renders (e.g. a video src resolves) the incoming layer is refreshed
 * without re-triggering the animation.
 *
 * Shared by the real web player and the editor preview so a previewed
 * transition matches device playback exactly.
 */
import React, {
  CSSProperties, ReactNode, useEffect, useRef, useState,
} from "react";
import { TransitionPlan, transitionCss } from "./transitionEngine";

interface Layer {
  key: React.Key;
  node: ReactNode;
}

type Phase = "idle" | "start" | "run";

export function TransitionStage({
  sceneKey,
  plan,
  children,
  className,
  style,
}: {
  sceneKey: React.Key;
  plan: TransitionPlan;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  const [incoming, setIncoming] = useState<Layer>({ key: sceneKey, node: children });
  const [outgoing, setOutgoing] = useState<Layer | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");

  const prevKeyRef = useRef<React.Key>(sceneKey);
  const incomingRef = useRef<Layer>(incoming);
  const childrenRef = useRef<ReactNode>(children);
  const planRef = useRef<TransitionPlan>(plan);
  const timerRef = useRef<number | undefined>(undefined);
  const raf1Ref = useRef<number | undefined>(undefined);
  const raf2Ref = useRef<number | undefined>(undefined);

  incomingRef.current = incoming;
  childrenRef.current = children;
  planRef.current = plan;

  const clearTimers = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    if (raf1Ref.current) cancelAnimationFrame(raf1Ref.current);
    if (raf2Ref.current) cancelAnimationFrame(raf2Ref.current);
  };

  // Refresh the incoming node when the SAME scene re-renders.
  useEffect(() => {
    if (sceneKey === prevKeyRef.current) {
      setIncoming({ key: sceneKey, node: children });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  // Drive the transition when the scene changes.
  useEffect(() => {
    if (sceneKey === prevKeyRef.current) return;
    const p = planRef.current;

    clearTimers();
    // The layer currently on screen becomes the outgoing layer.
    setOutgoing(incomingRef.current);
    setIncoming({ key: sceneKey, node: childrenRef.current });
    prevKeyRef.current = sceneKey;

    if (p.instant || p.durationMs <= 0) {
      setOutgoing(null);
      setPhase("idle");
      return;
    }

    setPhase("start");
    // Two rAFs so the browser paints the *From* styles before we flip to
    // *To*, guaranteeing the CSS transition actually runs.
    raf1Ref.current = requestAnimationFrame(() => {
      raf2Ref.current = requestAnimationFrame(() => setPhase("run"));
    });
    // Drop the outgoing layer a hair after the animation completes.
    timerRef.current = window.setTimeout(() => {
      setOutgoing(null);
      setPhase("idle");
    }, p.durationMs + 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneKey]);

  useEffect(() => () => clearTimers(), []);

  const layerBase: CSSProperties = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    willChange: "transform, opacity, filter",
    backfaceVisibility: "hidden",
  };

  const incomingStyle: CSSProperties = {
    ...layerBase,
    ...(phase === "start" ? plan.incomingFrom : phase === "run" ? plan.incomingTo : plan.incomingTo),
    transition: phase === "start" ? "none" : transitionCss(plan),
    zIndex: 2,
  };

  const outgoingStyle: CSSProperties = {
    ...layerBase,
    ...(phase === "start" ? plan.outgoingFrom : plan.outgoingTo),
    transition: phase === "start" ? "none" : transitionCss(plan),
    zIndex: 1,
  };

  return (
    <div
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        perspective: plan.perspective ? "1600px" : undefined,
        ...style,
      }}
    >
      {/* Raw scene keys (not slot-prefixed) so a scene that moves from the
          incoming to the outgoing slot keeps its DOM instance — a fading-out
          <video> keeps playing instead of remounting from 0. incoming.key and
          outgoing.key are always distinct steps, so keys stay unique. */}
      {outgoing && (
        <div key={outgoing.key} style={outgoingStyle}>
          {outgoing.node}
        </div>
      )}
      <div key={incoming.key} style={incomingStyle}>
        {incoming.node}
      </div>
    </div>
  );
}
